import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { 
  Calendar, 
  DollarSign, 
  FileText, 
  ArrowDownCircle, 
  ArrowUpCircle, 
  Wallet,
  Receipt,
  FileDown,
  FileSpreadsheet,
  FileCode
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";
import { downloadResponse } from "../../utils/downloadFile";

const FechoDiario = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [dadosFecho, setDadosFecho] = useState(null);
  const [paperFormat, setPaperFormat] = useState("A4");

  const [dataFecho, setDataFecho] = useState(new Date().toISOString().split('T')[0]);

  const fetchFechoDiario = async (dataSelecionada) => {
    try {
      setLoading(true);
      const response = await api.post("/caixa/fecho-diario", {
        empresa_id: empresa?.id || user?.empresa_id,
        data: dataSelecionada || dataFecho
      });
      setDadosFecho(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar o fecho diário:", err);
      notify("Erro ao carregar os dados de fecho diário.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.id || user?.empresa_id) {
      fetchFechoDiario(dataFecho);
    }
  }, [empresa?.id, user?.empresa_id]);

  const handleDateChange = (e) => {
    const novaData = e.target.value;
    setDataFecho(novaData);
    fetchFechoDiario(novaData);
  };

  // Funções de Exportação
  // eslint-disable-next-line no-unused-vars
  const handleExportPDF = async () => {
    try {
      await notify("A gerar relatório de fecho em PDF...", "info");
    } catch (err) {
      await notify("Erro ao exportar PDF.", "error");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleExportExcel = async () => {
    try {
      await notify("A gerar folha Excel do fecho diário...", "info");
    } catch (err) {
      await notify("Erro ao exportar Excel.", "error");
    }
  };

  const downloadExport = async (format) => {
    try {
      const response = await api.get(`/caixa/fecho-diario/export/${format}`, {
        params: {
          empresa_id: empresa?.id || user?.empresa_id,
          data: dataFecho,
          papel: paperFormat,
        },
        responseType: "blob",
      });
      downloadResponse(response, `fecho-diario-${dataFecho}.${format === "excel" ? "xls" : "pdf"}`);
      await notify("Fecho diario exportado com sucesso.", "success");
    } catch (err) {
      await notify(err.message || "Erro ao exportar fecho diario.", "error");
    }
  };

  const handleExportXML = async () => {
    try {
      const xmlData = JSON.stringify(dadosFecho, null, 2);
      const blob = new Blob([xmlData], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fecho_diario_${dataFecho}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      await notify("Ficheiro XML exportado com sucesso.", "success");
    } catch (err) {
      await notify("Erro ao exportar XML.", "error");
    }
  };

  const resumo = dadosFecho?.resumo_transacoes || {
    quantidade_documentos: 0,
    total_faturado_dia: 0,
    recebidos_dinheiro: 0,
    recebidos_outros_meios: 0,
    total_saidas_pagas: 0,
    saldo_final_caixa: 0
  };

  const documentosDia = dadosFecho?.documentos_dia || [];
  const despesasDia = dadosFecho?.despesas_dia || [];

  return (
    <ResourcePage 
      title="Fecho Diário de Caixa" 
      breadcrumb={["Gestão Financeira", "Fecho Diário"]}
      actions={(
        <div className="d-flex align-items-center gap-2">
          <select className="form-select form-select-sm" aria-label="Formato de impressão" value={paperFormat} onChange={(event) => setPaperFormat(event.target.value)}>
            <option value="A4">A4</option>
            <option value="A5">A5</option>
            <option value="TICKET">Ticket</option>
          </select>
          <button 
            type="button" 
            className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={() => downloadExport("pdf")}
          >
            <FileDown size={16} /> PDF
          </button>
          <button 
            type="button" 
            className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={() => downloadExport("excel")}
          >
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button 
            type="button" 
            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={handleExportXML}
          >
            <FileCode size={16} /> XML
          </button>
        </div>
      )}
    >
      {/* Seletor de Data */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Calendar size={18} /> Data do Fecho:
              </span>
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                value={dataFecho} 
                onChange={handleDateChange} 
              />
            </div>
            <div className="col-md-auto">
              {loading && <span className="text-muted small">A calcular fecho...</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Cartões Estatísticos do Fecho */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-primary bg-primary-subtle p-2 rounded d-inline-flex mb-3">
                <Receipt size={22} />
              </div>
              <p className="text-muted mb-1 small">Total Faturado no Dia</p>
              <h4 className="mb-0 text-primary fw-bold">{Number(resumo.total_faturado_dia).toLocaleString()} Kz</h4>
              <span className="text-muted small">{resumo.quantidade_documentos} documentos emitidos</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-success bg-success-subtle p-2 rounded d-inline-flex mb-3">
                <ArrowUpCircle size={22} />
              </div>
              <p className="text-muted mb-1 small">Recebidos em Dinheiro</p>
              <h4 className="mb-0 text-success fw-bold">{Number(resumo.recebidos_dinheiro).toLocaleString()} Kz</h4>
              <span className="text-muted small">Outros meios: {Number(resumo.recebidos_outros_meios).toLocaleString()} Kz</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-danger bg-danger-subtle p-2 rounded d-inline-flex mb-3">
                <ArrowDownCircle size={22} />
              </div>
              <p className="text-muted mb-1 small">Saídas / Despesas Pagas</p>
              <h4 className="mb-0 text-danger fw-bold">{Number(resumo.total_saidas_pagas).toLocaleString()} Kz</h4>
              <span className="text-muted small">Pagamentos efetuados</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className={`p-2 rounded d-inline-flex mb-3 ${resumo.saldo_final_caixa >= 0 ? 'text-success bg-success-subtle' : 'text-warning bg-warning-subtle'}`}>
                <Wallet size={22} />
              </div>
              <p className="text-muted mb-1 small">Saldo Final em Caixa</p>
              <h4 className={`mb-0 fw-bold ${resumo.saldo_final_caixa >= 0 ? 'text-success' : 'text-danger'}`}>
                {Number(resumo.saldo_final_caixa).toLocaleString()} Kz
              </h4>
              <span className="text-muted small">Balanço do dia</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabelas Detalhadas do Dia */}
      <div className="row">
        {/* Documentos Emitidos no Dia */}
        <div className="col-lg-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2 text-primary">
                <FileText size={18} /> Documentos Emitidos Hoje
              </h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Tipo / Nº</th>
                      <th>Estado</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentosDia.length > 0 ? (
                      documentosDia.map((doc) => (
                        <tr key={doc.id}>
                          <td><span className="fw-semibold">{doc.tipo}</span> {doc.numero}</td>
                          <td>
                            <span className={`badge ${doc.estado === "Validado" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}>
                              {doc.estado}
                            </span>
                          </td>
                          <td className="text-end fw-bold">{Number(doc.valor_pago || 0).toLocaleString()} Kz</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-3">Nenhum documento emitido nesta data.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Despesas Pagas no Dia */}
        <div className="col-lg-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2 text-danger">
                <ArrowDownCircle size={18} /> Despesas Pagas Hoje
              </h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Descrição</th>
                      <th>Estado</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {despesasDia.length > 0 ? (
                      despesasDia.map((desp) => (
                        <tr key={desp.id}>
                          <td>{desp.descricao}</td>
                          <td>
                            <span className="badge bg-success-subtle text-success">{desp.estado}</span>
                          </td>
                          <td className="text-end fw-bold text-danger">{Number(desp.valor || 0).toLocaleString()} Kz</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-3">Nenhuma despesa paga nesta data.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResourcePage>
  );
};

export default FechoDiario;
